import { DashboardUrl } from "@common";
import {
    AppState,
    campaignBudgetForm,
    campaignBudgetFormBindings,
    campaignCreativesForm,
    campaignMediaPlanForm,
    createOrUpdateCampaignAction,
    CurrencyMapping,
    EstimatedImpressionData,
    purchaseCampaignByIdAction,
    campaignMediaPlanFormForEstimatedImpression
} from "@store";
import * as cn from "classnames";
import { History, Location } from "history";
import { FetchingState, FETCH_STATE, FETCH_STATE_SUCCESS } from "rd-redux-http";
import * as React from "react";
import { connect, Dispatch } from "react-redux";
import { BudgetSliderComponent } from "./BudgetSlider";
import "./BudgetSlider.css";
import "./CampaignAsideComponent.css";
import { EstimatedImpressionsValue } from "./EstimatedImpressionComponent";

export interface CampaignAsideProps {
    isValid?: boolean;
    budgetForm?: typeof campaignBudgetForm.types.selectorResult;
    budgetFormEvents?: typeof campaignBudgetForm.types.eventBindings;
    currencyMapping: CurrencyMapping[];
    clientId: string;
    editCamapignId: string;
    estimatedImpression: EstimatedImpressionData;
    pricingModel: string;
    estimatedImpressionFetchState: FETCH_STATE;
    displayAsSummary: boolean;
    isError: boolean;
    canEstimate: boolean;
    onSubmit?: (clientId: string, editCamapignId: string) => void;
    onPurchase?: (clientId: string, editCamapignId: string) => void;
    onCancel?: () => void;
    returnToDashboard?: () => void;
}

export interface CampaignAsideState {
    value: number;
}

export class CampaignAsideComponent extends React.Component<CampaignAsideProps, CampaignAsideState> {
    constructor(props: CampaignAsideProps) {
        super(props);
        this.state = {
            value: 10
        };
    }

    render() {
        const {
            onCancel,
            onSubmit,
            budgetForm,
            budgetFormEvents,
            currencyMapping,
            editCamapignId,
            estimatedImpression,
            clientId,
            pricingModel,
            estimatedImpressionFetchState,
            displayAsSummary,
            returnToDashboard,
            isError,
            canEstimate
        } = this.props;

        if (isError) {
            return null;
        }

        return (
            <section className="budget">
                <div className="content">
                    <section className={cn("budget-header", displayAsSummary ? "budget-hidden" : "")} />
                    <section className={cn("budget-slider", displayAsSummary ? "budget-hidden" : "")}>
                        <BudgetSliderComponent
                            budgetForm={budgetForm}
                            budgetFormEvents={budgetFormEvents}
                            currencyMapping={currencyMapping}
                        />
                    </section>
                    <section className={cn("budget-summary", displayAsSummary ? "" : "budget-hidden")}>
                        <ul>
                            <li>
                                <p className="budget-title__summary">FULL BUDGET</p>
                                <p className="budget-value__summary">
                                    {budgetForm.fields.budget.value}{" "}
                                    <span className="budget-currency__summary">{budgetForm.fields.currency.value}</span>
                                </p>
                            </li>
                        </ul>
                    </section>
                    <section className="budget-info">
                        <ul>
                            <li>
                                <p className="budget-title">Pricing model</p>
                                <p className="budget-value">{pricingModel ? pricingModel : "-"}</p>
                            </li>
                            <li className="tour-campaign-estimated-value">
                                <p className="budget-title">
                                    {pricingModel === "CPC" ? "Estimated Clicks" : "Estimated Impressions"}
                                </p>
                                <EstimatedImpressionsValue
                                    estimatedImpression={estimatedImpression}
                                    estimatedImpressionFetchState={estimatedImpressionFetchState}
                                    isReviewComponent={false}
                                    isSummaryComponent={displayAsSummary ? true : false}
                                    canEstimate={canEstimate}
                                />
                            </li>
                        </ul>
                    </section>
                </div>
                <section className="control-buttons tour-campaign-save-cancel">
                    <input
                        className={cn(
                            "common-btn custom-budget-save-btn",
                            displayAsSummary ? "custom-budget-save-btn__hidden" : ""
                        )}
                        type="submit"
                        value="Save"
                        disabled={displayAsSummary ? true : false}
                        onClick={e => onSubmit(clientId, editCamapignId)}
                    />
                    <input
                        className={cn(
                            "common-btn custom-budget-cancel-btn",
                            displayAsSummary ? "custom-budget-cancel-btn__hidden" : ""
                        )}
                        type="reset"
                        value="Cancel"
                        disabled={displayAsSummary ? true : false}
                        onClick={e => onCancel()}
                    />
                    <input
                        className={cn(
                            "common-btn custom-budget-return-btn",
                            displayAsSummary ? "" : "custom-budget-cancel-btn__hidden"
                        )}
                        type="reset"
                        value="Back"
                        disabled={displayAsSummary ? false : true}
                        onClick={e => returnToDashboard()}
                    />
                </section>
            </section>
        );
    }
}

export interface CampaignAsideComponentProps {
    clientId: string;
    history: History;
    location: Location;
    displayAsSummary: boolean;
    isError: boolean;
}

export const CampaignAside = connect(
    (state: AppState, ownProps: CampaignAsideComponentProps): Partial<CampaignAsideProps> => {
        const mediaPlanForm = campaignMediaPlanForm.selector(state.campaignState.campaignMediaPlanForm);
        const creativesForm = campaignCreativesForm.selector(state.campaignState.campaignCreativesForm);
        const budgetForm = campaignBudgetForm.selector(state.campaignState.campaignBudgetForm);

        const currencyMapping = FetchingState.isSuccess(state.mappings.currencies) && state.mappings.currencies.data;

        const campaignMediaPlanFormForEstimatedImpressionSelectionResult = campaignMediaPlanFormForEstimatedImpression.selector(
            state.campaignState.campaignMediaPlanForm
        );
        const timetable = findTimetable(
            campaignMediaPlanFormForEstimatedImpressionSelectionResult.fields.timetable.value
        );

        const canEstimate: boolean = checkFullFirstPageCampaignForm(
            campaignMediaPlanFormForEstimatedImpressionSelectionResult,
            timetable
        );

        return {
            isValid: mediaPlanForm.isValid && creativesForm.isValid && budgetForm.isValid,
            budgetForm,
            isError: ownProps.isError,
            currencyMapping: currencyMapping || [],
            clientId: ownProps.clientId,
            editCamapignId:
                state.campaignState.editingCampaign.fetchState === FETCH_STATE_SUCCESS
                    ? state.campaignState.editingCampaign.data.result.campaignId
                    : undefined,
            estimatedImpression:
                state.campaignState.estimatedImpression.fetchState === FETCH_STATE_SUCCESS
                    ? state.campaignState.estimatedImpression.data
                    : {
                          sizing: 0,
                          pricePerUnit: 0,
                          status: undefined
                      },
            estimatedImpressionFetchState: state.campaignState.estimatedImpression.fetchState,
            pricingModel: mediaPlanForm.fields.priceModel.value,
            canEstimate
        };
    },
    (dispatch: Dispatch, ownProps: CampaignAsideComponentProps): Partial<CampaignAsideProps> => ({
        onSubmit: (clientId: string, editCamapignId: string) => {
            const mode = editCamapignId ? "update" : "create";
            dispatch(createOrUpdateCampaignAction({ clientId: clientId, campaignId: editCamapignId, mode: mode }));
        },
        onPurchase: (clientId: string, editCamapignId: string) => {
            dispatch(purchaseCampaignByIdAction({ clientId: clientId, campaignId: editCamapignId }));
        },
        onCancel: () => {
            ownProps.history.push(DashboardUrl.format({ clientId: ownProps.clientId }));
        },
        returnToDashboard: () => {
            ownProps.history.push(DashboardUrl.format({ clientId: ownProps.clientId }));
        },
        budgetFormEvents: campaignBudgetFormBindings.bind(dispatch, undefined)
    })
)(CampaignAsideComponent);

const datesMap = {
    Sun: "Sunday",
    Mon: "Monday",
    Tue: "Tuesday",
    Wed: "Wednesday",
    Thu: "Thursday",
    Fri: "Friday",
    Sat: "Saturday"
};

function findTimetable(data: any) {
    const timetable = data
        ? Object.keys(data)
              .map((tableKey: string, index: number) => {
                  const table = data[tableKey];
                  const tableKeys = Object.keys(table);
                  const selectedTableKeys = tableKeys.filter(key => table[key]);
                  return {
                      key: datesMap[tableKey],
                      allDay: selectedTableKeys.length == tableKeys.length,
                      selectedKeys: selectedTableKeys
                  };
              })
              .filter(table => table.allDay || table.selectedKeys.length)
        : undefined;

    return timetable;
}
function checkFullFirstPageCampaignForm(form: any, timetable: any) {
    const isFullFirstPageCampaignForm: boolean =
        form.isValid === true &&
        form.fields.language.value &&
        form.fields.country.value &&
        (form.fields.priceModel.value === "CPC" || (timetable && timetable.length > 0));

    return isFullFirstPageCampaignForm;
}
